import React, { useState } from 'react';
import { getUserContact } from '../services/userService';
import { useAuth } from '../context/AuthContext';
import { firstSigner } from '../services/signers';

// Seller utilities questionnaire: the TC reviews the email from her own account, edits it if needed, and sends it.
// Email opens with the seller's address filled in. Text opens a text to the seller's phone. Send marks the task done.

const isQuestionnaireTask = (name) => /^Send Utilities and Services Questionnaire to Seller/i.test(name || '');

const join = (...parts) => parts.filter(Boolean).join(' ');

const buildBody = (sellerFirst, secondFirst, tc) => {
  const greeting = secondFirst ? `${sellerFirst} and ${secondFirst}` : (sellerFirst || 'there');
  return `Hi ${greeting},

My name is ${tc.firstName || ''}, and I'm the Transaction Coordinator on your transaction. We are about two weeks from closing, so I'm reaching out to get your utilities and services squared away. Please reply with the following so I can share them with the buyer's team.

1. Electric: Who is your electric provider?
2. Gas: Do you have gas? If so, who is your provider?
3. Water: Who is your water provider?
4. Internet: Who is your internet provider?
5. Cable: Who is your cable provider?
6. Pool service: Do you have a pool? If so, who is your pool company?
7. Pest control: Who do you use?
8. Other services: Is there any other service you use, such as trash, security, or lawn care? If so, who is it?
9. Keys: Where will you leave the keys? We recommend leaving them on the kitchen counter. We will pick them up and deliver them for the final walk-through.

The fastest way to reach me is by text or phone at ${tc.phone || ''}. Just reply with your answers and I'll take it from there.

Thank you,
${tc.fullName || ''}
Transaction Coordinator, RESIDENCE | eXp Realty
${tc.email || ''}
${tc.phone || ''}`;
};

export default function SellerQuestionnaireQuickContact({ task, deal, onSent }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState('Utilities and services for your closing');
  const [body, setBody] = useState('');

  if (!isQuestionnaireTask(task?.name || task?.title)) return null;

  const sellerEmail = deal?.email || '';
  const sellerPhone = deal?.phone || '';
  const sellerFirst = deal?.firstName || '';
  const second = firstSigner(deal);

  const btn = { display: 'inline-block', padding: '8px 14px', borderRadius: 6, fontWeight: 700, fontSize: 13, textDecoration: 'none', border: '1px solid #B8962E', background: '#B8962E', color: '#FFFFFF', cursor: 'pointer', marginRight: 8 };
  const btnCancel = { ...btn, background: '#FFFFFF', color: '#5B6270', borderColor: '#D5D9E0' };

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setLoading(true);
    try {
      const tcContact = user?.uid ? await getUserContact(user.uid).catch(() => null) : null;
      const tc = {
        firstName: tcContact?.firstName || '',
        fullName: join(tcContact?.firstName, tcContact?.lastName),
        phone: tcContact?.phone || '',
        email: tcContact?.email || '',
      };
      setBody(buildBody(sellerFirst, second.firstName || '', tc));
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const digits = sellerPhone.replace(/[^0-9+]/g, '');
  const done = () => { if (onSent) onSent(); };

  return (
    <div style={{ marginTop: 8, padding: 10, border: '1px solid #E5E0D3', borderRadius: 6, background: '#FFFFFF' }}>
      <div style={{ fontSize: 13, color: '#1A1A1A', fontWeight: 600, marginBottom: 6 }}>
        Seller: {join(sellerFirst, deal?.lastName) || 'No seller saved'}{second.firstName ? ` and ${second.firstName}` : ''}
      </div>
      <button type="button" onClick={toggle} disabled={loading} style={btn}>
        {loading ? 'Loading...' : open ? 'Hide email' : 'Review and send questionnaire'}
      </button>
      {open && (
        <div style={{ marginTop: 10 }}>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject" style={{ width: '100%', boxSizing: 'border-box', padding: 8, marginBottom: 8, fontSize: 13 }} />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={18} style={{ width: '100%', boxSizing: 'border-box', padding: 8, fontSize: 13 }} />
          <div style={{ marginTop: 8 }}>
            {sellerEmail && <a href={`mailto:${sellerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`} onClick={done} style={btn}>Send email</a>}
            {digits && <a href={`sms:${digits}?body=${encodeURIComponent(body)}`} onClick={done} style={btn}>Send text</a>}
            <button type="button" onClick={() => setOpen(false)} style={btnCancel}>Cancel</button>
          </div>
          {!sellerEmail && <div style={{ fontSize: 12, color: '#5B6270', marginTop: 6 }}>No seller email saved on this deal.</div>}
        </div>
      )}
    </div>
  );
}
